import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Status } from '@/features/bidding/constants'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { buildBidPriceConfirmationLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'

import { useInvalidateBidOverviewSummaryPageQuery } from './useBidOverviewSummaryPageQuery'
import { useInvalidateGroupedBidsPageQuery } from './useGroupedBidsPageQuery'
import { useSaveBidsMutation } from './useSaveBidsMutation'

type UseBidOfferPriceSaveActionsParams = {
  groupParams: GroupedBidParams
}

type UseBidOfferPriceSaveActionsReturn = {
  saveBidsAndNavigate: (bids: ExtendedBid[]) => Promise<void>
  isSaving: boolean
}

export function useBidOfferPriceSaveActions(
  params: UseBidOfferPriceSaveActionsParams,
): UseBidOfferPriceSaveActionsReturn {
  const { groupParams } = params
  const navigate = useNavigate()
  const { saveBids, isPending } = useSaveBidsMutation()
  const invalidateCachedQueryStateOfGroupedBids = useInvalidateGroupedBidsPageQuery()
  const invalidateCachedQueryStateOfBidOverview = useInvalidateBidOverviewSummaryPageQuery()

  /**
   * Drafts and already-sent bids are posted separately.
   *
   * The bids here keep whatever status they arrived with, so a day where one activation group was
   * already sent and another is still a draft would otherwise put both in one
   * POST /bid/capacity/bulk. Portfolio Manager skips its offer validations for a request that is
   * entirely drafts, and a mixed one used to drag the draft entries through checks meant for offers -
   * including a PTU-count parity check that throws rather than returning a validation error, so a
   * 500 on a save. PM now filters per entry, and splitting here makes the mixed request impossible
   * regardless of which side deploys first.
   *
   * The draft batch's status is left alone: forcing DRAFT would make "save prices" un-send a bid that
   * has already been offered.
   *
   * /grouped-bid returns each bid history's newest-created row whatever status it carries, and under
   * PM's schedules model the accepted slot is created at first acknowledgement and stays the
   * newest-created row forever after - a re-offer updates the OFFERED slot in place without touching
   * its createdAt. So a day that was already acknowledged arrives here as ACCEPTED, and PM's status
   * gate on POST /bid/capacity/bulk 400s the entire request unless every entry is DRAFT or OFFERED.
   * Editing prices on an acknowledged day is itself a re-offer, so everything non-draft is coerced to
   * OFFERED before it is posted - a re-post with otherwise unchanged content diffs to a no-op on PM's
   * side.
   */
  const saveBidsAndNavigate = async (bids: ExtendedBid[]) => {
    const draftBids = bids.filter((bid) => bid.status === Status.DRAFT)
    const sentBids = bids
      .filter((bid) => bid.status !== Status.DRAFT)
      .map((bid) => ({ ...bid, status: Status.OFFERED }))

    for (const batch of [draftBids, sentBids]) {
      if (batch.length > 0) {
        await saveBids(batch)
      }
    }

    await invalidateCachedQueryStateOfGroupedBids()
    await invalidateCachedQueryStateOfBidOverview()
    navigate(buildBidPriceConfirmationLink(groupParams))
  }

  return useMemo(
    () => ({
      saveBidsAndNavigate,
      isSaving: isPending,
    }),
    [saveBidsAndNavigate, isPending],
  )
}
